import { Cow, CowRecord, DairyRecord } from '../models/types';
import { SPACE_REGEX, REGEX_NUMERIC, REGEX_DECIMAL, ONLY_ALPHABETS_NO_SPACE, ONLY_ALPHABETS_WITH_SPACE, ALPHANUMERIC_WITHOUT_SPACE, ALPHANUMERIC_WITH_SPACE, USERNAME_AT_FILTER_REGEX } from '../constants/CommonConstants';

export const removeSpaces = (text: string): string => {
    return text.replace(SPACE_REGEX, '');
};

export const numericValidation = (text: string): string => {
    return text.replace(REGEX_NUMERIC, '');
};

export const decimalValidation = (text: string): string => {
    // Remove all non-numeric and non-decimal characters
    let cleaned = text.replace(REGEX_DECIMAL, '');

    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    return cleaned;
};

export const removeNumbers = (text: string): string => {
    return text.replace(/[0-9]/g, '');
};

export const alphabetOnlyNoSpace = (text: string): string => {
    return text.replace(ONLY_ALPHABETS_NO_SPACE, '');
};

export const alphabetOnlyWithSpace = (text: string): string => {
    return text.replace(ONLY_ALPHABETS_WITH_SPACE, '');
};

export const alphanumericWithoutSpace = (text: string): string => {
    return text.replace(ALPHANUMERIC_WITHOUT_SPACE, '');
};

export const alphanumericWithSpace = (text: string): string => {
    return text.replace(ALPHANUMERIC_WITH_SPACE, '');
};

export const usernameAtFilterRRegex = (text: string): string => {
    return text.replace(USERNAME_AT_FILTER_REGEX, '');
};

export const generateId = (prefix: string = ''): string => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `${prefix}${timestamp}${random}`;
};

export const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

export const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

export const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

export const parseMilkQuantityToLiters = (input: string): number => {
    const trimmed = input.trim();
    if (!trimmed) return 0;

    const parts = trimmed.split('.');
    const liters = parseInt(parts[0], 10);
    const safeLiters = isNaN(liters) ? 0 : liters;

    if (parts.length === 1) return safeLiters;

    const frac = parts[1] || '';
    const gramsRaw = frac.padEnd(3, '0').slice(0, 3);
    const grams = parseInt(gramsRaw, 10);
    const safeGrams = isNaN(grams) ? 0 : grams;

    return safeLiters + safeGrams / 1000;
};

export const parseDate = (dateString: string): Date => {
    // Expects DD/MM/YYYY format
    const parts = dateString.split('/');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }
    return new Date(dateString);
};

export const getISODateString = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

export const validateCowId = (id: string): boolean => {
    return /^COW\d{3,}$/.test(id);
};

export const calculateAge = (dateOfBirth: string): string => {
    const today = new Date();
    const dob = new Date(dateOfBirth);
    const years = today.getFullYear() - dob.getFullYear();
    const months = today.getMonth() - dob.getMonth();

    let ageYears = years;
    let ageMonths = months;

    if (months < 0) {
        ageYears--;
        ageMonths = 12 + months;
    }

    if (ageYears === 0) {
        return `${ageMonths} month${ageMonths !== 1 ? 's' : ''}`;
    } else if (ageMonths === 0) {
        return `${ageYears} year${ageYears !== 1 ? 's' : ''}`;
    } else {
        return `${ageYears} year${ageYears !== 1 ? 's' : ''}, ${ageMonths} month${ageMonths !== 1 ? 's' : ''}`;
    }
};

export const getCowStatusColor = (status: string): string => {
    switch (status) {
        case 'active':
            return '#4CAF50';
        case 'sold':
            return '#FFA726';
        case 'deceased':
            return '#D32F2F';
        default:
            return '#9E9E9E';
    }
};

export const getRecordTypeIcon = (
    recordType: string,
): { name: string; color: string } => {
    switch (recordType) {
        case 'fodder':
            return { name: 'grass', color: '#8BC34A' };
        case 'medicine':
            return { name: 'medical-bag', color: '#E91E63' };
        case 'milk':
            return { name: 'cup', color: '#2196F3' };
        default:
            return { name: 'file-document', color: '#9E9E9E' };
    }
};

export const getDairyRecordTypeIcon = (
    recordType: string,
): { name: string; color: string } => {
    switch (recordType) {
        case 'purchase':
            return { name: 'cart-plus', color: '#FF9800' };
        case 'sale':
            return { name: 'cash-multiple', color: '#4CAF50' };
        default:
            return { name: 'file-document', color: '#9E9E9E' };
    }
};
